/**
 * `/sessions/:id/door/registration`: the registration door — opening a
 * follow-up case for a probably-unregistered household, with the person's
 * explicit consent to be relaunched by e-mail (and, once the gateway is
 * wired up, SMS) (`docs/API.md` §7).
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../../../api/client';
import { createRegistrationCase } from '../../../api/household';
import { Badge, Button, Card, Checkbox, Input, useToast } from '../../../components/ui';
import { ConsentBox } from '../components/ConsentBox';
import { MailSequence } from '../components/MailSequence';
import { OutcomeChoice } from '../components/OutcomeChoice';
import type { OutcomeChoiceOption } from '../components/OutcomeChoice';
import { StarRating } from '../components/StarRating';
import { t } from '../../../i18n/fr';
import { asRegistrationDoorState } from '../navigation';
import { phantomConfidenceLabel } from '../../session/utils/phantom';

import type { ApiErrorBody, RegistrationCase } from '../../../api/types';

/**
 * A local, presentation-only constat: `docs/API.md` §7's
 * `RegistrationCaseInputRequest` carries no such field — the case itself is
 * either opened (with consent) or it isn't — but the field screen still
 * wants the volunteer to record what they found at the door before doing so.
 */
type Constat = 'info_given' | 'already_registered' | 'refusal' | 'empty';

const CONSTAT_OPTIONS: OutcomeChoiceOption<Constat>[] = [
  { value: 'info_given', label: t('household.registration.outcomes.info_given') },
  { value: 'already_registered', label: t('household.registration.outcomes.already_registered') },
  { value: 'refusal', label: t('household.registration.outcomes.refusal') },
  { value: 'empty', label: t('household.registration.outcomes.empty') },
];

/**
 * Render the registration door screen.
 *
 * @returns The page element.
 */
export function RegistrationDoorPage() {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const doorState = asRegistrationDoorState(location.state);

  const [constat, setConstat] = useState<Constat>('info_given');
  const [quality, setQuality] = useState<number | null>(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdCase, setCreatedCase] = useState<RegistrationCase | null>(null);
  // Offline (or the immediate flush just missed the network): the case is
  // safe in the queue, but there is no server row — and so no follow-up
  // sequence — to show yet (`docs/OFFLINE.md` §2).
  const [queued, setQueued] = useState(false);

  if (!doorState) {
    return (
      <Card>
        <h1>{t('household.sheet.noAssignment.title')}</h1>
        <p>{t('household.sheet.noAssignment.body')}</p>
        <Link to={`/sessions/${sessionId}`}>{t('household.sheet.noAssignment.back')}</Link>
      </Card>
    );
  }

  const destination =
    doorState.housingType === 'building'
      ? `/sessions/${sessionId}/building`
      : `/sessions/${sessionId}`;
  // Captured locally: the `doorState !== undefined` narrowing above does
  // not carry into `onSubmit`, a function declared further down this same
  // render.
  const doorId = doorState.doorId;
  const visitId = doorState.visitId;

  async function onSubmit() {
    if (!consentGiven) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createRegistrationCase(doorId, {
        first_name: firstName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        consent: true,
        visit: visitId ?? null,
      });
      if ('queued' in result) {
        setQueued(true);
        showToast(t('household.registration.queuedCase'), 'info');
      } else {
        setCreatedCase(result);
      }
    } catch (cause) {
      if (cause instanceof ApiError) {
        const body = cause.body as ApiErrorBody | undefined;
        setError(body?.detail ?? t('household.registration.consentRequiredError'));
      } else {
        setError(t('household.registration.consentRequiredError'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (queued) {
    return (
      <div className="household-screen">
        <div className="bloc">
          <div className="adr">{doorState.addressLabel}</div>
          <p className="muted">{doorState.doorLabel}</p>
        </div>
        <div className="bloc">
          <p className="kicker">{t('household.registration.queuedCase')}</p>
          <p className="muted">{t('household.registration.queuedBody')}</p>
        </div>
        <div className="bloc">
          <Button variant="primary" fullWidth onClick={() => navigate(destination)}>
            {t('household.registration.continue')}
          </Button>
        </div>
      </div>
    );
  }

  if (createdCase) {
    return (
      <div className="household-screen">
        <div className="bloc">
          <div className="adr">{doorState.addressLabel}</div>
          <p className="muted">{doorState.doorLabel}</p>
        </div>
        <div className="bloc">
          <p className="kicker">{t('household.registration.sequenceKicker')}</p>
          <p>
            {t('household.registration.messagesPlanned', {
              count: createdCase.messages.length,
              plural: createdCase.messages.length === 1 ? '' : 's',
            })}
          </p>
          <MailSequence messages={createdCase.messages} />
        </div>
        <div className="bloc">
          <Button variant="primary" fullWidth onClick={() => navigate(destination)}>
            {t('household.registration.continue')}
          </Button>
        </div>
      </div>
    );
  }

  const confidenceLabel = phantomConfidenceLabel(doorState.confidence ?? null);

  return (
    <div className="household-screen">
      <div className="bloc">
        <div className="adr">{doorState.addressLabel}</div>
        <p className="muted">{doorState.doorLabel}</p>
      </div>

      <div className="bloc">
        <div className="phantom-card">
          <Badge tone="yellow">{t('household.phantomCard.title')}</Badge>
          {doorState.hypothesis ? (
            <>
              <p style={{ margin: 'var(--space-2) 0 0' }}>{doorState.hypothesis}</p>
              {confidenceLabel ? (
                <p className="muted" style={{ margin: 'var(--space-1) 0 0' }}>
                  {t('household.phantomCard.confidence', { confidence: confidenceLabel })}
                </p>
              ) : null}
            </>
          ) : (
            <p style={{ margin: 'var(--space-2) 0 0' }}>{t('household.phantomCard.generic')}</p>
          )}
        </div>
      </div>

      <div className="bloc">
        <p className="kicker">{t('household.registration.constatKicker')}</p>
        <OutcomeChoice
          options={CONSTAT_OPTIONS}
          value={constat}
          onChange={setConstat}
          ariaLabel={t('household.registration.constatKicker')}
          disabled={submitting}
        />
        <p className="kicker" style={{ marginTop: 'var(--space-4)' }}>
          {t('household.sheet.qualityKicker')}
        </p>
        <StarRating value={quality} onChange={setQuality} disabled={submitting} />
      </div>

      <div className="bloc">
        <p className="kicker">{t('household.registration.relanceKicker')}</p>
        <ConsentBox>{t('household.registration.consent')}</ConsentBox>
        <Checkbox
          label={t('household.registration.consentCheckbox')}
          checked={consentGiven}
          onChange={(event) => setConsentGiven(event.target.checked)}
          disabled={submitting}
        />
        <Input
          label={t('household.registration.firstNameLabel')}
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          disabled={submitting}
        />
        <Input
          label={t('household.registration.emailLabel')}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={submitting}
        />
        <Input
          label={t('household.registration.phoneLabel')}
          type="tel"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          hint={t('household.registration.phoneHint')}
          disabled={submitting}
        />
        <p className="kicker">{t('household.registration.sequenceKicker')}</p>
        <div className="mail">
          <span className="quand">{t('household.cases.step.j2')}</span>
          <span className="quoi">{t('household.registration.sequenceJ2')}</span>
        </div>
        <div className="mail">
          <span className="quand">{t('household.cases.step.j7')}</span>
          <span className="quoi">{t('household.registration.sequenceJ7')}</span>
        </div>
        <div className="mail">
          <span className="quand">{t('household.cases.step.j14')}</span>
          <span className="quoi">{t('household.registration.sequenceJ14')}</span>
        </div>
        <p className="rappel" style={{ marginTop: 'var(--space-3)' }}>
          ⚠ {t('household.registration.reminder')}
        </p>
      </div>

      {error ? (
        <div className="bloc">
          <p className="rappel" role="alert">
            {error}
          </p>
        </div>
      ) : null}

      <div className="bloc">
        <Button
          variant="primary"
          fullWidth
          disabled={submitting || !consentGiven}
          onClick={() => void onSubmit()}
        >
          {submitting ? t('household.sheet.submitting') : t('household.registration.submit')}
        </Button>
      </div>
    </div>
  );
}
